import { Box, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { Message, MessageInput, PendingBoMessage } from "../../layer06";
import { useScrollToLatestMessage } from "../../../common";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllChatbotConversations,
  selectChatbotMessages,
  selectSendMessageStatus,
  selectThreadId,
  sendGeneralMessage,
  setCoachBotPrompt,
} from "app/containers/Chatbot/slice";

export const ChatWithBoActivityDetailSection = () => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectChatbotMessages);
  const threadId = useAppSelector(selectThreadId);
  const sendMessageStatus = useAppSelector(selectSendMessageStatus);
  const allConversations = useAppSelector(selectAllChatbotConversations);
  const scrollContainerRef = useScrollToLatestMessage(messages?.length ?? 0);

  // The Check In screen writes a check-in thread into this same redux slice,
  // so a check-in conversation can still be active when the user navigates
  // back here and must not be shown as the "Chat with Bo" conversation.
  const isCurrentThreadCheckIn = allConversations.some(
    (c) => c.threadId === threadId && c.weeklyCheckInLink
  );

  // Bootstrap a self-chat thread with Bo the first time this screen is opened,
  // or if the active thread is actually a check-in thread left over from the
  // Check In screen (which shares this same redux slice). The nav bar disables
  // switching between Check In and Chat with Bo while a thread is being
  // generated, so this can't race with Check In's own thread switch.
  useEffect(() => {
    if (!threadId || isCurrentThreadCheckIn) {
      dispatch(setCoachBotPrompt(null));
    }
  }, [dispatch, threadId, isCurrentThreadCheckIn]);

  const [messageTimestamps, setMessageTimestamps] = useState<DateTime[]>([]);

  // The chatbot API doesn't return a per-message timestamp, so we stamp
  // each message locally as it first appears in state.
  useEffect(() => {
    const count = messages?.length ?? 0;
    setMessageTimestamps((prev) => {
      if (count <= prev.length) {
        return prev;
      }
      const next = [...prev];
      for (let i = prev.length; i < count; i++) {
        next.push(DateTime.local());
      }
      return next;
    });
  }, [messages?.length]);

  return (
    <Box
      display="flex"
      sx={{
        paddingX: {
          xs: ".5rem",
          sm: "1.5rem",
        },
        height: {
          xs: "calc(100vh - 6.5rem)",
          sm: "calc(100vh - 4.5rem)",
        },
        boxSizing: "border-box",
      }}
    >
      {/* MESSAGES */}
      <Box
        ref={scrollContainerRef}
        flex="1 1 0"
        minWidth={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          height: "100%",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            height: "3rem",
            marginBottom: "-3rem",
            zIndex: 2,
            pointerEvents: "none",
            background: (theme) =>
              `linear-gradient(to bottom, ${theme.palette.background.default}, ${theme.palette.background.default}00)`,
          }}
        />
        <Stack
          direction="column"
          paddingTop="1.5rem"
          paddingBottom="6rem"
          flex="1 1 0"
          rowGap="1rem"
        >
          {(messages ?? []).map((node, i) => (
            <Message
              key={`${node.role}-${i}`}
              role={node.role === "assistant" ? "BO" : "USER"}
              content={node.content}
              sentTimeStamp={messageTimestamps[i] ?? DateTime.local()}
              toolCalls={node.toolCalls}
            />
          ))}
          <PendingBoMessage />
        </Stack>
        <MessageInput
          onSend={(message) => {
            if (sendMessageStatus === "loading") {
              return false;
            }
            dispatch(sendGeneralMessage({ message }));
          }}
          note={{
            variant: "info",
            text: "Conversation is private.",
          }}
        />
      </Box>
    </Box>
  );
};

